/* PAGINA 4 · DETTAGLIO TRADEBOT
 *
 * Le quattro domande, in quest'ordine: quanto vale, sono dentro, quanto sto
 * perdendo adesso, come lo fermo. Lo stop sta in fondo e vuole due tocchi.
 *
 * Due cose che questa schermata deve dire e che il backend non dice da solo:
 *  - quanto sono vecchi questi numeri. La domanda si legge su `lastSeenAt` —
 *    l'ultima volta che il PROCESSO era vivo — e non su `updatedAt`, che dice
 *    solo quando è cambiata una colonna. La differenza non è accademica: un bot
 *    in posizione da cinque giorni non cambia niente per definizione, e la
 *    schermata gli scriveva addosso «non decidere su questi numeri» mentre
 *    faceva esattamente il suo mestiere. Tutto il giudizio sta in
 *    `lib/vitalita.ts`, che è anche dove è scritto il perché;
 *  - "fermo" non è la risposta a `PATCH isRunning:false`. Fra la richiesta e
 *    l'arresto vero c'è un intervallo in cui il bot è ancora dentro: si scrive
 *    "in arresto", non "fermo".
 *
 * In fondo, sotto il corpo, c'è il modulo che rifà la misura di QUESTA
 * configurazione su un periodo scelto: `ProvaConfigurazione`.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Avvertenza,
  Avvisi,
  AzioneDistruttiva,
  ContenitoreDati,
  DatoVivo,
  Distintivo,
  Divisore,
  GraficoEquity,
  IconaFreccia,
  IntestazionePagina,
  Occhiello,
  Pulsante,
  Scheda,
  TernaMetriche,
  usaAvvisi,
  type StatoDatoVivo,
} from '../../componenti';
import { eStantio, tradebots, usaRichiesta, wallets } from '../../api';
import type { Trade, Tradebot } from '../../api';
import { dataOra, daQuanto, num, ora, pct, timeframe, valutaFirmata } from '../../lib/formato';
import { tonoRendimento } from '../../lib/metriche';
import {
  coppiaBot,
  equityDaTrade,
  modalitaBot,
  periodoBot,
  posizione,
  sedeBot,
  statoBot,
  ternaBot,
} from './modello';
import { capitaleDi, vitalitaDi, type CapitaleBot, type StatoBattito } from '../../lib/vitalita';
import {
  DistintiviModalita,
  EtichettaStato,
  IconaClona,
  IconaConPallino,
  IconaModifica,
} from './Comuni';
import { RisultatoOperazioni } from '../../componenti/RisultatoOperazioni';
import { serieDaTradebot } from '../../lib/operazioni';
import { PAGINE_DETTAGLIO, usaCorrelazioni } from './correlazione';
import { IndicatoriBacktest } from './Indicatori';
import { FoglioClona } from './FoglioClona';
import { FoglioModifica } from './FoglioModifica';
import { ProvaConfigurazione } from './ProvaConfigurazione';
import './tradebot.css';

/** Quante operazioni mostrare per volta. Non esiste `GET /trades` paginato
 *  (RICHIESTE-API.md §4): arrivano tutte insieme e si impaginano qui. */
const PASSO_OPERAZIONI = 25;

/** Ogni quanto ricontrollare un bot a cui è stato chiesto di fermarsi. */
const MS_ATTESA_ARRESTO = 5000;

export function DettaglioTradebot() {
  const { id } = useParams();
  const naviga = useNavigate();
  const { avvisi, mostra } = usaAvvisi();

  const idBot = Number(id);
  const idValido = Number.isFinite(idBot) && idBot > 0;

  const [arrestoRichiestoA, setArrestoRichiestoA] = useState<Date | null>(null);
  const [inTransizione, setInTransizione] = useState(false);
  const [quanteOperazioni, setQuanteOperazioni] = useState(PASSO_OPERAZIONI);
  const [clonaAperto, setClonaAperto] = useState(false);
  const [modificaAperta, setModificaAperta] = useState(false);

  const richiesta = usaRichiesta(() => tradebots.uno(idBot), [idBot], {
    attiva: idValido,
    eVuoto: (b) => b == null,
  });

  const bot = richiesta.dati;

  /* Finché il processo non conferma, si ricontrolla. Senza questo ciclo la
     schermata resterebbe ferma su "in arresto" per sempre, o peggio direbbe
     "fermo" a un bot che è ancora dentro una posizione. */
  const ricarica = richiesta.ricarica;
  const inEsecuzione = bot?.isRunning;

  useEffect(() => {
    if (!arrestoRichiestoA) return;
    if (inEsecuzione === false) {
      setArrestoRichiestoA(null);
      return;
    }
    const t = setInterval(ricarica, MS_ATTESA_ARRESTO);
    return () => clearInterval(t);
  }, [arrestoRichiestoA, inEsecuzione, ricarica]);

  async function ferma() {
    if (!bot || inTransizione) return;
    setInTransizione(true);
    try {
      await tradebots.ferma(bot.id);
      setArrestoRichiestoA(new Date());
      mostra('Arresto richiesto: il processo lo raccoglie alla prossima candela', 'neutro');
      richiesta.ricarica();
    } catch (e) {
      mostra(e instanceof Error ? e.message : 'Arresto non riuscito', 'fallito');
    } finally {
      setInTransizione(false);
    }
  }

  async function avvia() {
    if (!bot || inTransizione) return;
    setInTransizione(true);
    try {
      await tradebots.avvia(bot.id);
      setArrestoRichiestoA(null);
      mostra('Avvio richiesto: il bot entra in warm-up', 'neutro');
      richiesta.ricarica();
    } catch (e) {
      mostra(e instanceof Error ? e.message : 'Avvio non riuscito', 'fallito');
    } finally {
      setInTransizione(false);
    }
  }

  if (!idValido) {
    return (
      <div className="sb-cornice">
        <IntestazionePagina titolo="Tradebot" />
        <div style={{ padding: 'var(--sb-pad-pagina)' }}>
          <Avvertenza livello="grave">
            L’indirizzo non contiene un identificativo di bot valido.
          </Avvertenza>
        </div>
      </div>
    );
  }

  return (
    <div className="sb-cornice">
      <div style={{ padding: 'var(--sb-pad-pagina)', paddingBottom: 0 }}>
        <Pulsante variante="nudo" onClick={() => naviga('/tradebot')}>
          <span style={{ display: 'inline-flex', transform: 'rotate(90deg)' }} aria-hidden="true">
            <IconaFreccia />
          </span>
          Tutti i tradebot
        </Pulsante>
      </div>

      <ContenitoreDati
        stato={richiesta.stato}
        causaVuoto={`Il bot #${idBot} non esiste più: potrebbe essere stato eliminato.`}
        azioneVuoto={{ etichetta: 'Torna alla lista', onClick: () => naviga('/tradebot') }}
        messaggioErrore={richiesta.errore?.message}
        onRiprova={richiesta.ricarica}
      >
        {bot && (
          <CorpoDettaglio
            bot={bot}
            arrestoRichiestoA={arrestoRichiestoA}
            letturaA={richiesta.letturaA}
            inTransizione={inTransizione}
            quanteOperazioni={quanteOperazioni}
            onAltreOperazioni={() => setQuanteOperazioni((q) => q + PASSO_OPERAZIONI)}
            onFerma={ferma}
            onAvvia={avvia}
            onClona={() => setClonaAperto(true)}
            onModifica={() => setModificaAperta(true)}
            onRicarica={richiesta.ricarica}
          />
        )}
      </ContenitoreDati>

      <FoglioClona
        bot={clonaAperto ? bot : null}
        aperta={clonaAperto && bot != null}
        onChiudi={() => setClonaAperto(false)}
        onCreati={(quanti) => mostra(quanti === 1 ? '1 bot creato' : `${quanti} bot creati`)}
      />

      <FoglioModifica
        bot={modificaAperta ? bot : null}
        aperta={modificaAperta && bot != null}
        onChiudi={() => setModificaAperta(false)}
        onApplicato={(quante) => {
          setModificaAperta(false);
          richiesta.ricarica();
          mostra(
            quante === 1
              ? '1 modifica salvata · attiva dalla prossima candela'
              : `${quante} modifiche salvate · attive dalla prossima candela`,
          );
        }}
      />

      <Avvisi avvisi={avvisi} />
    </div>
  );
}

/* ==========================================================================
 * Il corpo, quando il bot c'è
 * ======================================================================== */

function CorpoDettaglio({
  bot,
  arrestoRichiestoA,
  letturaA,
  inTransizione,
  quanteOperazioni,
  onAltreOperazioni,
  onFerma,
  onAvvia,
  onClona,
  onModifica,
  onRicarica,
}: {
  bot: Tradebot;
  arrestoRichiestoA: Date | null;
  letturaA: Date | null;
  inTransizione: boolean;
  quanteOperazioni: number;
  onAltreOperazioni: () => void;
  onFerma: () => void;
  onAvvia: () => void;
  onClona: () => void;
  onModifica: () => void;
  onRicarica: () => void;
}) {
  const stato = statoBot(bot, arrestoRichiestoA);
  const modalita = modalitaBot(bot);
  const pos = posizione(bot);
  const terna = ternaBot(bot);
  const p = periodoBot(bot);
  const punti = useMemo(() => equityDaTrade(bot), [bot]);

  /* Il capitale valutato al prezzo più recente conosciuto, con la fonte del
     prezzo dichiarata. Prima si sommava la sola valuta e un bot con 0,0254 BTCB
     e cinque centesimi di USDT risultava «0,00 USDT · parziale»: esatto e
     inutile, e al posto del numero giusto. */
  const capitale = capitaleDi(bot);

  /* Il backtest che giustifica questo bot si riconosce dai parametri, non da un
     id di provenienza: qui si scava più a fondo che in lista, perché il bot è
     uno solo e vale la pena spendere qualche pagina di ricerca in più. */
  const correlazioni = usaCorrelazioni([bot], PAGINE_DETTAGLIO);
  const correlazione = correlazioni.dati?.get(bot.id);

  /* I numeri di questa schermata non li abbiamo letti noi: li ha scritti il
     processo del bot. Quanto siano vecchi lo dice il BATTITO (`lastSeenAt`),
     non `updatedAt`: vedi `lib/vitalita.ts`. */
  const vitalita = vitalitaDi(bot);
  const prezzo = notaPrezzo(capitale, bot, vitalita.visto);

  const operazioni = useMemo(
    () =>
      [...(bot.trades ?? [])].sort(
        (a, b) => dataDi(b.buyDate ?? b.createdAt) - dataDi(a.buyDate ?? a.createdAt),
      ),
    [bot.trades],
  );

  return (
    <>
      {/* ---------- intestazione ---------- */}
      <IntestazionePagina
        titolo={coppiaBot(bot)}
        sotto={
          <div>
            <div className="tb-testata__titolo">
              <IconaConPallino strategia={bot.strategyName} stato={stato} dim={26} />
              <DistintiviModalita bot={bot} />
              <EtichettaStato stato={stato} />
            </div>
            <div className="tb-sotto">
              {bot.strategyName} · {sedeBot(bot)} · #{bot.id} · {p.etichetta}
              {p.durata ? ` · ${p.durata}` : ''}
            </div>
            {stato.nota && <div className="tb-stato-nota">{stato.nota}</div>}
            {/* Da quanto il processo è stato visto vivo. Sta accanto allo stato
                perché «in marcia» da solo è una riga di database: dice che
                qualcuno ha scritto `isRunning`, non che il bot respiri. Con
                «Non respira» si tace: quella frase è già dentro `stato.nota`, e
                scriverla due volte di fila la fa leggere come rumore. */}
            {bot.isRunning && stato.chiave !== 'non-respira' && (
              <div className="tb-stato-nota">{vitalita.testo}</div>
            )}
          </div>
        }
        azioni={
          <div className="sb-riga" style={{ gap: 'var(--sb-s-3)' }}>
            <Pulsante onClick={onModifica}>
              <IconaModifica />
              Modifica dal vivo
            </Pulsante>
            <Pulsante variante="tenue" onClick={onClona}>
              <IconaClona />
              Clona
            </Pulsante>
          </div>
        }
      />

      <div className="tb-dettaglio">
        {/* ================= colonna principale ================= */}
        <div className="tb-colonna">
          {/* ---------- posizione ---------- */}
          <div className={`tb-posizione${pos.aperta ? ' tb-posizione--dentro' : ''}`}>
            <div className="sb-riga" style={{ justifyContent: 'space-between' }}>
              <Occhiello>
                {pos.aperta ? '● In posizione · long' : '○ Fuori mercato · tutto in valuta'}
              </Occhiello>
              <DatoVivo
                valore={ETICHETTA_BATTITO[vitalita.stato]}
                meta={
                  vitalita.visto
                    ? `${vitalita.testo} · ultimo battito alle ${ora(vitalita.visto)}`
                    : vitalita.testo
                }
                stato={STATO_VIVO[vitalita.stato]}
              />
            </div>

            <div className="tb-posizione__valore">
              <span className="tb-posizione__cifra">
                {capitale.valore == null ? 'n/d' : `${num(capitale.valore, 2)} ${bot.currency}`}
              </span>
              {/* La cifra parziale porta la parola accanto, non solo la nota
                  sotto: è più bassa del vero, e chi legge solo il numero grosso
                  deve vedere subito che non è tutto. */}
              {capitale.valore != null && capitale.parziale && (
                <Distintivo tono="cautela">parziale</Distintivo>
              )}
              {pos.variazionePct != null && (
                <span
                  className={`sb-numero sb-tono-${tonoRendimento(pos.variazionePct)}`}
                  style={{ fontSize: 'var(--sb-t-16)' }}
                >
                  {pct(pos.variazionePct, 2)}
                </span>
              )}
            </div>

            {/* La percentuale accanto alla cifra è la posizione APERTA
                dall'ingresso, non il risultato del bot: dirlo qui evita che si
                legga come il profitto complessivo, che sta nella terna. */}
            <div className="sb-nota" style={{ marginTop: 'var(--sb-s-3)' }}>
              {capitale.valore == null
                ? (capitale.motivo ?? 'il capitale non è calcolabile')
                : 'capitale governato dal bot'}
              {prezzo ? ` · ${prezzo}` : ''}
              {capitale.valore != null && capitale.motivo ? ` · ${capitale.motivo}` : ''}
              {pos.aperta ? ' · la percentuale è la posizione aperta dall’ingresso' : ''}
            </div>

            <div className="tb-tre">
              <div>
                <div className="tb-tre__etichetta">INGRESSO</div>
                <div className="tb-tre__valore">
                  {pos.prezzoIngresso != null ? num(pos.prezzoIngresso, 6) : '—'}
                </div>
              </div>
              <div>
                <div className="tb-tre__etichetta">ULTIMO PREZZO VISTO</div>
                <div className="tb-tre__valore">
                  {pos.prezzoScritto != null ? num(pos.prezzoScritto, 6) : '—'}
                </div>
              </div>
              <div>
                <div className="tb-tre__etichetta">NON REALIZZATO</div>
                <div
                  className={`tb-tre__valore sb-tono-${tonoRendimento(pos.variazioneValuta)}`}
                >
                  {pos.variazioneValuta != null
                    ? valutaFirmata(pos.variazioneValuta, '', 2)
                    : '—'}
                </div>
              </div>
            </div>

            {!pos.aperta && (
              <div className="sb-nota" style={{ marginTop: 'var(--sb-s-5)' }}>
                Nessuna operazione aperta: il capitale è fermo in {bot.currency}. Le soglie di
                uscita non hanno effetto finché il bot non rientra.
              </div>
            )}

            {/* L'avvertenza compare SOLO quando c'è davvero qualcosa da guardare.
                Prima si accendeva su `updatedAt`, e un bot in posizione da cinque
                giorni — che per definizione non cambia niente — se la prendeva
                addosso mentre era il più sano dell'insieme. */}
            {vitalita.allarme && (
              <div style={{ marginTop: 'var(--sb-s-6)' }}>
                {/* Grave anche prima delle due ore quando c'è denaro vero a
                    mercato: lì il ritardo del battito non è un dato vecchio, è
                    una posizione aperta che nessuno sta sorvegliando. */}
                <Avvertenza
                  livello={
                    vitalita.stato === 'muto' || (modalita.denaroReale && pos.aperta)
                      ? 'grave'
                      : 'cautela'
                  }
                >
                  {maiuscola(vitalita.testo)}.{' '}
                  {modalita.denaroReale && pos.aperta
                    ? 'C’è una posizione aperta con denaro vero e il processo non dà segno di sé: nessuno la sta sorvegliando.'
                    : 'Questi prezzi e questi saldi non sono di adesso: non decidere su questi numeri.'}{' '}
                  <button
                    type="button"
                    onClick={onRicarica}
                    style={{
                      all: 'unset',
                      cursor: 'pointer',
                      color: 'var(--sb-accento-chiaro)',
                      textDecoration: 'underline',
                      minHeight: 'var(--sb-tocco-min)',
                    }}
                  >
                    Rileggi
                  </button>
                </Avvertenza>
              </div>
            )}
          </div>

          {/* ---------- equity ---------- */}
          <Scheda>
            <div className="sb-riga" style={{ justifyContent: 'space-between' }}>
              <strong>Capitale del bot</strong>
              <span className="sb-numero sb-tono-perdita" style={{ fontSize: 'var(--sb-t-11)' }}>
                {terna.drawdown == null ? 'drawdown n/d' : `massimo drawdown −${num(terna.drawdown, 1)}%`}
              </span>
            </div>
            <div className="sb-nota" style={{ margin: 'var(--sb-s-3) 0 var(--sb-s-5)' }}>
              {p.etichetta} · {terna.operazioni == null ? 'n/d' : num(terna.operazioni)} operazioni ·
              ricostruito dai saldi di uscita, non da una serie salvata dal backend
            </div>
            <GraficoEquity
              punti={punti}
              causaVuoto="Il bot non ha ancora chiuso due operazioni: non c’è una curva da disegnare."
            />
          </Scheda>

          {/* ---------- operazioni ---------- */}
          <Scheda>
            <div className="sb-riga" style={{ justifyContent: 'space-between' }}>
              <strong>Operazioni</strong>
              <span className="sb-nota">
                {operazioni.length === 0
                  ? 'nessuna'
                  : `${num(Math.min(quanteOperazioni, operazioni.length))} di ${num(operazioni.length)}`}
              </span>
            </div>

            {operazioni.length === 0 ? (
              <div className="sb-nota" style={{ marginTop: 'var(--sb-s-5)' }}>
                Il bot non ha ancora aperto nessuna operazione.
              </div>
            ) : (
              <>
                <div className="sb-scorri-x" style={{ marginTop: 'var(--sb-s-5)' }}>
                  <table className="sb-tabella" style={{ minWidth: 460 }}>
                    <thead>
                      <tr>
                        <th>Stato</th>
                        <th>Entrata</th>
                        <th>Uscita</th>
                        <th>Risultato</th>
                      </tr>
                    </thead>
                    <tbody>
                      {operazioni.slice(0, quanteOperazioni).map((t) => (
                        <RigaOperazione key={t.id} trade={t} />
                      ))}
                    </tbody>
                  </table>
                </div>

                {quanteOperazioni < operazioni.length && (
                  <div style={{ marginTop: 'var(--sb-s-5)' }}>
                    <Pulsante largo onClick={onAltreOperazioni}>
                      Mostra altre {Math.min(PASSO_OPERAZIONI, operazioni.length - quanteOperazioni)}
                    </Pulsante>
                  </div>
                )}

                <div className="sb-nota" style={{ marginTop: 'var(--sb-s-5)' }}>
                  Le operazioni arrivano tutte insieme dentro la riga del bot: non esiste un
                  endpoint per elencarle, filtrarle per data o impaginarle lato server. Vedi
                  RICHIESTE-API.md §4.
                </div>
              </>
            )}
          </Scheda>
        </div>

        {/* ================= colonna laterale ================= */}
        <div className="tb-colonna">
          <Scheda>
            <Occhiello>Come va, e su che periodo</Occhiello>
            <div style={{ marginTop: 'var(--sb-s-5)' }}>
              <TernaMetriche
                profitto={terna.profitto}
                drawdown={terna.drawdown}
                operazioni={terna.operazioni}
                periodo={p.etichetta}
                durata={p.durata}
                densita="piena"
              />
            </div>
            {/* Da dove parte il periodo. Non è pignoleria: il rendimento si
                misura da quando il bot ha fissato la misura, che è DOPO il
                warm-up. Scrivere «dal 30 ago» accanto a un numero che parte dal
                5 set è mettere il numero sotto l'etichetta sbagliata. */}
            <div className="sb-nota" style={{ marginTop: 'var(--sb-s-5)' }}>
              {p.fonteInizio === 'misura'
                ? 'Il periodo parte da quando il bot ha fissato la misura del rendimento, non da quando è stato creato: fra le due c’è il warm-up.'
                : p.fonteInizio === 'ignota'
                  ? 'Il bot non porta nessuna data: questo periodo non è misurato.'
                  : 'Il bot non ha ancora registrato da quando misura il rendimento: il periodo parte dall’avvio, che precede la misura di tutto il warm-up.'}
            </div>

            {terna.profitto == null && (
              <div className="sb-nota" style={{ marginTop: 'var(--sb-s-3)' }}>
                Profitto non ancora misurabile: {motivoProfittoMancante(bot)}.
              </div>
            )}

            {bot.marketChange != null && terna.profitto != null && (
              <div className="sb-nota" style={{ marginTop: 'var(--sb-s-5)' }}>
                Il mercato nello stesso periodo: {pct(bot.marketChange, 2)}.
              </div>
            )}
            {terna.motivoDrawdownMancante && (
              <div className="sb-nota" style={{ marginTop: 'var(--sb-s-3)' }}>
                Drawdown non calcolabile: {terna.motivoDrawdownMancante}.
              </div>
            )}
          </Scheda>

          {/* Tenuta, Nei ribassi e Intorno non misurano il bot: misurano il
              backtest da cui viene, su un altro periodo. Il periodo è scritto
              dentro ogni scheda, e la provenienza sopra a tutte e tre. */}
          <Scheda>
            <div className="sb-riga" style={{ justifyContent: 'space-between' }}>
              <Occhiello>Come regge questa configurazione</Occhiello>
              <Pulsante variante="nudo" onClick={correlazioni.ricarica}>
                Ricerca
              </Pulsante>
            </div>
            <div className="sb-colonna" style={{ marginTop: 'var(--sb-s-5)' }}>
              <IndicatoriBacktest correlazione={correlazione} stato={correlazioni.stato} />
            </div>
          </Scheda>

          {/* Le operazioni VERE del bot, nella stessa forma con cui si guardano quelle di un
              backtest. Sta subito sotto gli indicatori della configurazione perche' e' li' che
              il confronto ha senso: sopra c'e' come quella configurazione ha reso in prova,
              qui cosa sta facendo davvero. Un bot che entra ed esce in momenti diversi da come
              aveva fatto in prova e' la cosa piu' importante da notare, e prima non c'era modo
              di accorgersene.
              
              Le operazioni di un bot sono pochissime -- quello in produzione ne ha una, aperta
              e non chiusa -- e il componente lo regge: un marcatore solo e una riga sola sono
              lo stato normale qui, non un guasto. La serie dei prezzi il bot non la registra,
              quindi il grafico lo dichiara e mostra l'elenco. */}
          <RisultatoOperazioni serie={serieDaTradebot(bot)} />

          {bot.walletId != null && <SaldiCatenaBot bot={bot} />}

          <Scheda>
            <Occhiello>Configurazione</Occhiello>
            <div className="tb-voci" style={{ marginTop: 'var(--sb-s-5)' }}>
              <Voce chiave="Strategia" valore={bot.strategyName} />
              <Voce chiave="Coppia" valore={coppiaBot(bot)} />
              <Voce chiave="Candela" valore={timeframe(bot.candleSize)} />
              <Voce chiave="Warm-up" valore={`${num(bot.warmupPeriod)} candele`} />
              <Voce chiave="Capitale" valore={modalita.denaroReale ? 'denaro vero' : 'simulato'} />
              <Voce chiave="Sede" valore={sedeBot(bot)} />
              {bot.walletId != null && (
                <>
                  <Voce chiave="DEX" valore={bot.dex} />
                  <Voce chiave="Catena" valore={bot.chainId != null ? String(bot.chainId) : '—'} />
                  <Voce
                    chiave="Slippage massimo"
                    valore={bot.swapSlippage != null ? `${num(bot.swapSlippage, 2)}%` : '—'}
                  />
                </>
              )}
              <Voce
                chiave="Saldo parziale"
                valore={bot.usePartialBalance ? 'sì, limitato' : 'no, usa tutto il conto'}
              />
              {bot.strategyParams &&
                Object.entries(bot.strategyParams).map(([k, v]) => (
                  <Voce key={k} chiave={k} valore={formattaParametro(v)} />
                ))}
            </div>
            <Divisore />
            <Pulsante largo onClick={onModifica}>
              <IconaModifica />
              Cambia questi parametri dal vivo
            </Pulsante>
          </Scheda>

          <Scheda>
            <Occhiello>Errori riportati dal bot</Occhiello>
            {(bot.errors?.length ?? 0) === 0 ? (
              <div className="sb-nota" style={{ marginTop: 'var(--sb-s-5)' }}>
                Nessun errore registrato.
              </div>
            ) : (
              <div style={{ marginTop: 'var(--sb-s-5)' }}>
                {bot.errors?.map((e, i) => (
                  <div className="tb-evento" key={`${i}-${e.slice(0, 24)}`}>
                    <span className="tb-evento__punto" aria-hidden="true" />
                    <span className="tb-evento__testo">{e}</span>
                  </div>
                ))}
                <div className="sb-nota" style={{ marginTop: 'var(--sb-s-4)' }}>
                  Gli errori sono un elenco di stringhe senza data né ordine: non si può dire
                  quando sono successi né se sono ancora attuali. Vedi RICHIESTE-API.md §6.
                </div>
              </div>
            )}
            {bot.failedTransactions != null && bot.failedTransactions > 0 && (
              <div style={{ marginTop: 'var(--sb-s-5)' }}>
                <Avvertenza livello="cautela">
                  {num(bot.failedTransactions)} transazioni fallite dall’avvio.
                </Avvertenza>
              </div>
            )}
          </Scheda>
        </div>
      </div>

      {/* ---------- subito sotto il corpo: rifare la misura ----------
          Sta qui e non in fondo perché in fondo c'è l'arresto, che è
          l'azione irreversibile e va lasciata ultima sotto il pollice. */}
      <ProvaConfigurazione
        bot={bot}
        correlazione={correlazione}
        statoCorrelazione={correlazioni.stato}
      />

      {/* ---------- in fondo, dove arriva il pollice ---------- */}
      <div style={{ padding: 'var(--sb-pad-pagina)', borderTop: '1px solid var(--sb-bordo-tenue)' }}>
        {bot.isRunning ? (
          <AzioneDistruttiva
            etichetta={arrestoRichiestoA ? 'Arresto già richiesto' : 'Ferma il bot'}
            etichettaArmata="Tocca di nuovo per fermarlo"
            nota={
              pos.aperta
                ? 'La posizione aperta RESTA aperta: fermare il bot non la liquida, la lascia a mercato senza nessuno che la sorvegli.'
                : 'Il bot smette di decidere. Il capitale resta dov’è.'
            }
            onConferma={onFerma}
            disabilitata={inTransizione}
          />
        ) : (
          <div>
            <Pulsante variante="primario" largo onClick={onAvvia} disabled={inTransizione}>
              Avvia il bot
            </Pulsante>
            <div className="sb-nota" style={{ marginTop: 'var(--sb-s-4)' }}>
              {modalita.denaroReale
                ? 'Opera con denaro vero: dall’avvio compra e vende davvero, dopo il warm-up.'
                : 'Opera in simulazione: nessun ordine reale viene inviato.'}
            </div>
          </div>
        )}

        {letturaA && (
          <div className="sb-nota" style={{ marginTop: 'var(--sb-s-5)' }}>
            Riga letta dal server alle {ora(letturaA)}.
          </div>
        )}
      </div>
    </>
  );
}

/* ==========================================================================
 * Saldi e gas del wallet, per i bot su catena
 * ======================================================================== */

function SaldiCatenaBot({ bot }: { bot: Tradebot }) {
  const richiesta = usaRichiesta(
    (segnale) => wallets.saldiTutteLeCatene(bot.walletId as number, {}, segnale),
    [bot.walletId],
    { attiva: bot.walletId != null },
  );

  const catena = richiesta.dati?.chains.find((c) => c.chainId === bot.chainId);
  const stantio = eStantio(richiesta.letturaA);

  /* I cinque stati del dato vivo, tutti raggiungibili: caricamento, letto,
     stantìo, nodo muto, gas insufficiente. Il gas insufficiente è uno stato a
     sé perché il capitale può bastare e la catena no. */
  const stato: StatoDatoVivo =
    richiesta.stato === 'caricamento'
      ? 'caricamento'
      : richiesta.stato === 'errore' || (catena && !catena.ok)
        ? 'muto'
        : catena?.gasAlert
          ? 'insufficiente'
          : stantio
            ? 'stantio'
            : 'letto';

  return (
    <Scheda>
      <div className="sb-riga" style={{ justifyContent: 'space-between' }}>
        <Occhiello>Gas sulla catena</Occhiello>
        <Pulsante variante="nudo" onClick={richiesta.ricarica}>
          Rileggi
        </Pulsante>
      </div>

      <div style={{ marginTop: 'var(--sb-s-5)' }}>
        <DatoVivo
          valore={
            catena?.native
              ? `${num(Number(catena.native.balance), 5)} ${catena.nativeSymbol}`
              : richiesta.stato === 'caricamento'
                ? 'lettura…'
                : 'non letto'
          }
          meta={
            richiesta.letturaA
              ? `${catena?.name ?? `catena ${bot.chainId}`} · letto alle ${ora(richiesta.letturaA)}`
              : `catena ${bot.chainId}`
          }
          stato={stato}
        />
      </div>

      {catena?.gasAlert && (
        <div style={{ marginTop: 'var(--sb-s-5)' }}>
          <Avvertenza livello={catena.gasAlert.level === 'low' ? 'cautela' : 'grave'}>
            {catena.gasAlert.message} Copre {num(catena.gasAlert.swapsCovered)} swap; il minimo
            consigliato è {num(catena.gasAlert.recommendedMinimum, 5)} {catena.nativeSymbol}. Senza
            gas il bot non può firmare né entrare né uscire.
          </Avvertenza>
        </div>
      )}

      {catena && !catena.ok && (
        <div style={{ marginTop: 'var(--sb-s-5)' }}>
          <Avvertenza livello="grave">
            {catena.error ?? 'Il nodo RPC non risponde: il saldo non è verificabile.'}
          </Avvertenza>
        </div>
      )}

      {richiesta.stato === 'errore' && (
        <div style={{ marginTop: 'var(--sb-s-5)' }}>
          <Avvertenza livello="grave">
            {richiesta.errore?.message ?? 'Lettura on-chain non riuscita.'}
          </Avvertenza>
        </div>
      )}
    </Scheda>
  );
}

/* ==========================================================================
 * Pezzi minori
 * ======================================================================== */

/* Il battito, tradotto nelle due cose che la schermata deve mostrare: la
   parola grossa accanto al pallino e il colore del pallino stesso. «fermo» è
   grigio e non verde: un bot spento non ha numeri di adesso, ma non è un
   guasto. «non-misurabile» è grigio per la stessa ragione — non si afferma
   freschezza che non è stata misurata. */
const ETICHETTA_BATTITO: Record<StatoBattito, string> = {
  vivo: 'visto ora',
  fermo: 'fermo',
  'in-ritardo': 'battito in ritardo',
  muto: 'nessun segno di vita',
  'non-misurabile': 'battito non misurato',
};

const STATO_VIVO: Record<StatoBattito, StatoDatoVivo> = {
  vivo: 'letto',
  fermo: 'stantio',
  'in-ritardo': 'stantio',
  muto: 'muto',
  'non-misurabile': 'stantio',
};

function maiuscola(t: string): string {
  return t.charAt(0).toUpperCase() + t.slice(1);
}

/**
 * A che prezzo è stato valutato l'asset, e da dove viene quel prezzo.
 *
 * Cambia quanto ci si può fidare della cifra grossa: il prezzo del battito è di
 * minuti fa, quello dell'avvio può essere vecchio di mesi. Si scrive solo quando
 * c'è davvero dell'asset da valutare: su un bot fuori mercato il prezzo non
 * entra nel totale e nominarlo suggerirebbe il contrario.
 */
function notaPrezzo(c: CapitaleBot, bot: Tradebot, visto: Date | null): string | null {
  if (c.prezzo == null || c.fontePrezzo == null) return null;
  if (!bot.assetBalance) return null;
  const base = `${bot.asset} valutato a ${num(c.prezzo, 6)} ${bot.currency}`;
  /* Per le altre due fonti il perché diffidarne lo scrive già `capitaleDi` in
     `motivo`, che questa schermata stampa subito dopo: ripeterlo qui darebbe la
     stessa avvertenza due volte nella stessa riga. */
  if (c.fontePrezzo === 'battito') {
    return `${base}, il prezzo che il bot vedeva${visto ? ` ${daQuanto(visto)}` : ''}`;
  }
  return base;
}

/**
 * Perché il profitto non c'è.
 *
 * Un «n/d» muto si legge come «lo sappiamo ma non ve lo diciamo», e sulla
 * schermata che serve a decidere se lasciare acceso un bot è la risposta
 * peggiore. Le due cause sono distinte e possono presentarsi insieme: nessuna
 * operazione chiusa (non c'è ancora niente da misurare) e misura non fissata
 * (il bot non ha registrato il capitale di partenza, quindi manca il termine di
 * paragone).
 */
function motivoProfittoMancante(bot: Tradebot): string {
  const cause: string[] = [];
  const chiuse = (bot.trades ?? []).filter((t) => t.sellDate != null).length;
  if (chiuse === 0) cause.push('nessuna operazione è stata ancora chiusa');
  if (bot.initialValue == null) {
    cause.push(
      'la misura non è ancora stata fissata — il bot non ha registrato il capitale di partenza, quindi manca il termine di paragone',
    );
  }
  if (cause.length === 0) {
    return 'il bot non ha scritto né il rendimento né i saldi da cui ricavarlo';
  }
  return cause.join(' e ');
}

function Voce({ chiave, valore }: { chiave: string; valore: string }) {
  return (
    <div className="tb-voce">
      <span className="tb-voce__chiave">{chiave}</span>
      <span className="tb-voce__valore">{valore}</span>
    </div>
  );
}

function RigaOperazione({ trade }: { trade: Trade }) {
  const chiusa = trade.sellDate != null;
  const cambio = trade.change;

  return (
    <tr>
      <td style={{ textAlign: 'left', fontFamily: 'var(--sb-font-testo)' }}>
        <span className={`tb-stato-etichetta sb-tono-${chiusa ? 'neutro' : 'guadagno'}`}>
          {chiusa ? 'chiusa' : '● aperta'}
        </span>
      </td>
      <td style={{ textAlign: 'left' }} className="sb-tono-neutro">
        {trade.buyDate ? dataOra(trade.buyDate) : '—'}
        <div className="sb-tabella__sotto">
          {trade.buyPrice != null ? num(trade.buyPrice, 6) : '—'}
        </div>
      </td>
      <td style={{ textAlign: 'left' }} className="sb-tono-neutro">
        {trade.sellDate ? dataOra(trade.sellDate) : 'ancora aperta'}
        <div className="sb-tabella__sotto">
          {trade.sellPrice != null ? num(trade.sellPrice, 6) : '—'}
        </div>
      </td>
      <td className={`sb-tono-${tonoRendimento(cambio)}`}>
        {cambio == null ? 'n/d' : pct(cambio, 2)}
      </td>
    </tr>
  );
}

function formattaParametro(v: unknown): string {
  if (v == null) return '—';
  if (typeof v === 'number') return num(v, Number.isInteger(v) ? 0 : 4);
  if (typeof v === 'boolean') return v ? 'sì' : 'no';
  if (typeof v === 'string') return v;
  return JSON.stringify(v);
}

function dataDi(v: string | null | undefined): number {
  if (!v) return 0;
  const t = new Date(v).getTime();
  return Number.isNaN(t) ? 0 : t;
}
